import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * ModernSettingsSection — a titled group of settings rows inside one card.
 * Pair with ModernSettingsRow to get the organized, table-like settings feel
 * (think Linear / Stripe settings) instead of ad-hoc per-page layouts.
 */
export function ModernSettingsSection({
  title,
  description,
  icon,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--mui-shadow-card)]',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3 px-5 py-4">
        <div className="flex items-start gap-3">
          {icon && (
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[calc(var(--radius)-6px)] bg-muted text-muted-foreground">
              {icon}
            </span>
          )}
          <div>
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            {description && (
              <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        </div>
        {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div className="divide-y divide-border border-t border-border">{children}</div>
    </section>
  );
}
